import * as React from 'react';

import {
  Chat,
  Conversation,
  Composer,
  Indicators,
  Message,
  MessageGroup,
  MessageList,
} from '@mui/x-chat/headless';
import {
  createEchoAdapter,
  cloneMessages,
} from 'docs/data/chat/headless/examples/shared/demoUtils';
import {
  createLongThreadMessages,
  demoUsers,
} from 'docs/data/chat/headless/examples/shared/demoData';
import {
  demoLocaleText,
  demoMessageListSlotProps,
  demoSlotProps,
  DemoScrollToBottomOverlay,
  DemoToolbarButton,
} from 'docs/data/chat/headless/examples/shared/DemoPrimitives';

function createRealtimeController() {
  let emit = null;

  return {
    adapter: {
      ...createEchoAdapter({ agent: demoUsers.agent }),
      subscribe({ onEvent }) {
        emit = onEvent;
        return () => {
          emit = null;
        };
      },
    },
    push(event) {
      emit?.(event);
    },
  };
}

const seedMessages = cloneMessages(
  createLongThreadMessages('indicators').slice(-14),
);

export default function IndicatorsInContext() {
  const controller = React.useMemo(() => createRealtimeController(), []);
  const [messages, setMessages] = React.useState(seedMessages);
  const [conversation, setConversation] = React.useState({
    id: 'indicators',
    title: 'Indicator thread',
    subtitle: 'Typing, unread boundaries, and scroll state',
    participants: [demoUsers.you, demoUsers.alice, demoUsers.agent],
    unreadCount: 3,
    readState: 'unread',
  });

  const triggerTyping = React.useCallback(() => {
    controller.push({
      type: 'typing',
      conversationId: 'indicators',
      userId: demoUsers.alice.id,
      isTyping: true,
    });

    setTimeout(() => {
      controller.push({
        type: 'typing',
        conversationId: 'indicators',
        userId: demoUsers.alice.id,
        isTyping: false,
      });
    }, 1500);
  }, [controller]);

  const appendIncoming = React.useCallback(() => {
    setMessages((previous) => [
      ...previous,
      {
        id: `indicators-live-${previous.length + 1}`,
        conversationId: 'indicators',
        role: 'assistant',
        status: 'sent',
        author: demoUsers.alice,
        createdAt: new Date(
          Date.UTC(2026, 2, 15, 12, previous.length),
        ).toISOString(),
        parts: [
          {
            type: 'text',
            text: 'New activity arrived while the reader was away from the bottom of the list.',
          },
        ],
      },
    ]);
    setConversation((previous) => ({
      ...previous,
      unreadCount: previous.unreadCount + 1,
      readState: 'unread',
    }));
  }, []);

  return (
    <Chat.Root
      adapter={controller.adapter}
      conversations={[conversation]}
      initialActiveConversationId="indicators"
      messages={messages}
      onMessagesChange={setMessages}
      localeText={demoLocaleText}
      slotProps={{
        root: {
          style: {
            background: '#ffffff',
            display: 'grid',
            gap: 14,
          },
        },
      }}
    >
      <Conversation.Root
        slotProps={{
          root: {
            style: {
              height: 640,
              display: 'grid',
              gridTemplateRows: 'auto minmax(0, 1fr) auto',
              gap: 14,
            },
          },
        }}
      >
        <Conversation.Header
          slotProps={{
            header: demoSlotProps.conversationHeader,
          }}
        >
          <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
            <div>
              <Conversation.Title style={demoSlotProps.conversationTitle} />
              <Conversation.Subtitle style={demoSlotProps.conversationSubtitle} />
            </div>
            <Indicators.TypingIndicator
              slotProps={{
                root: {
                  style: {
                    background: '#fafafa',
                    color: '#2e7d32',
                    padding: '4px 10px',
                    fontSize: 12,
                    fontWeight: 600,
                  },
                },
              }}
            />
          </div>
          <Conversation.HeaderActions
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
          >
            <DemoToolbarButton onClick={triggerTyping}>
              Show typing
            </DemoToolbarButton>
            <DemoToolbarButton onClick={appendIncoming}>
              Append incoming
            </DemoToolbarButton>
            <DemoToolbarButton
              tone="accent"
              onClick={() =>
                setConversation((previous) => ({
                  ...previous,
                  unreadCount: 0,
                  readState: 'read',
                }))
              }
            >
              Mark read
            </DemoToolbarButton>
          </Conversation.HeaderActions>
        </Conversation.Header>
        <MessageList.Root
          estimatedItemSize={108}
          renderItem={({ id, index }) => (
            <React.Fragment key={id}>
              <Indicators.UnreadMarker
                index={index}
                messageId={id}
                slotProps={{
                  root: demoSlotProps.unreadMarkerRoot,
                  label: demoSlotProps.unreadMarkerLabel,
                }}
              />
              <MessageList.DateDivider
                formatDate={(date) =>
                  new Intl.DateTimeFormat('en-US', {
                    month: 'short',
                    day: 'numeric',
                  }).format(date)
                }
                index={index}
                messageId={id}
                slotProps={{
                  divider: demoSlotProps.dateDividerRoot,
                  label: demoSlotProps.dateDividerLabel,
                }}
              />
              <MessageGroup
                index={index}
                messageId={id}
                slotProps={{
                  group: demoSlotProps.messageGroupRoot,
                  authorName: demoSlotProps.messageGroupAuthorName,
                }}
              >
                <Message.Root
                  messageId={id}
                  slotProps={{
                    root: demoSlotProps.message,
                  }}
                >
                  <Message.Avatar
                    slotProps={{
                      avatar: demoSlotProps.avatar,
                      image: demoSlotProps.messageAvatarImage,
                    }}
                  />
                  <Message.Content
                    slotProps={{
                      bubble: demoSlotProps.messageBubble,
                    }}
                  />
                  <Message.Meta
                    slotProps={{
                      meta: demoSlotProps.meta,
                    }}
                  />
                </Message.Root>
              </MessageGroup>
            </React.Fragment>
          )}
          overlay={<DemoScrollToBottomOverlay />}
          slotProps={{
            ...demoMessageListSlotProps,
          }}
        />
        <Composer.Root
          slotProps={{
            root: demoSlotProps.composer,
          }}
        >
          <Composer.TextArea
            aria-label="Message"
            placeholder="Type a message"
            slotProps={{
              input: demoSlotProps.composerTextArea,
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Composer.SendButton
              data-variant="primary"
              slotProps={{
                sendButton: demoSlotProps.send,
              }}
            >
              Send
            </Composer.SendButton>
          </div>
        </Composer.Root>
      </Conversation.Root>
    </Chat.Root>
  );
}
